import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useNoIndex from "@/lib/useNoIndex";
import {
  AlertCircle, Loader2, Calendar, Clock, MapPin, Sparkles, Scissors, Heart,
} from "lucide-react";
import { API } from "@/lib/api";
import PhotoLightbox from "@/components/PhotoLightbox";

export default function PublicTrial() {
  useNoIndex();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/public/trial/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body?.detail || "Link non valido");
        }
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#F8F5F1" }}
      >
        <Loader2 className="w-8 h-8 animate-spin" strokeWidth={1.5} style={{ color: "#C8A97E" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: "#F8F5F1" }}
      >
        <div
          className="rounded-md p-10 max-w-md text-center"
          style={{
            backgroundColor: "#FFFDFA",
            boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 22px 60px -32px rgba(122,98,68,0.16)",
          }}
          data-testid="public-trial-error"
        >
          <AlertCircle className="w-10 h-10 mx-auto mb-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} />
          <h1
            className="font-serif-heading"
            style={{ fontStyle: "italic", fontSize: "2rem", color: "#2C2C2C" }}
          >
            Link non valido
          </h1>
          <p className="text-sm mt-2" style={{ color: "#7B6A45" }}>
            {error || "Il link condiviso non è valido o è stato revocato."}
          </p>
        </div>
      </div>
    );
  }

  const KindIcon = data.kind === "hair" ? Scissors : Sparkles;
  const kindLabel = data.kind === "hair" ? "capelli" : "trucco";
  const photos = data.photos || [];
  // Files / attachments are intentionally NOT exposed on the public trial
  // page — see the prior CTA cleanup. The trial surface is a pure
  // emotional / visual experience; documents are shared by the operator
  // through dedicated channels (email/WhatsApp).
  const heroBrandName = (data.owner_brand_name || data.owner_name || "").trim();
  const brideName = (data.client_name || "").trim();
  // The trial page intentionally does NOT link to the public quote. The
  // operator shares the quote separately via WhatsApp/email — surfacing
  // a CTA here would distract the bride from focusing on the trial
  // experience itself (photos, look, emotional close).

  // Evocative bullets that sit under "Cosa abbiamo definito durante la prova".
  // Used as a graceful default since the trial endpoint does not surface
  // free-form notes — keeps the section emotional without inventing data.
  const trialBullets = data.kind === "hair"
    ? [
        "L'acconciatura più adatta al Suo stile e al volto",
        "L'armonia con il velo, gli accessori e l'abito",
        "La tenuta e i prodotti pensati per la giornata",
      ]
    : [
        "I toni e le texture che valorizzano la Sua pelle",
        "L'intensità giusta tra luce naturale e fotografia",
        "Tenuta e ritocchi pensati per ogni momento del giorno",
      ];

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#F8F5F1", color: "#4A4A4A" }}
      data-testid="public-trial-page"
    >
      {/* Soft, intentional fade-in keeps the page from feeling "loaded".
          Same easing as the public quote page for visual consistency. */}
      <style>{`
        @keyframes ptFadeUp {
          0%   { opacity: 0; transform: translateY(14px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes ptFadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        .pt-fade-up { animation: ptFadeUp 900ms ease-out both; }
        .pt-fade-in { animation: ptFadeIn 900ms ease-out both; }
        .pt-d-1 { animation-delay: 80ms; }
        .pt-d-2 { animation-delay: 220ms; }
        .pt-d-3 { animation-delay: 360ms; }
        .pt-d-4 { animation-delay: 520ms; }
        .pt-d-5 { animation-delay: 700ms; }
      `}</style>

      <div className="max-w-3xl mx-auto pt-20 sm:pt-28 pb-14 px-5">

        {/* === HERO === */}
        <header
          className="text-center mb-14 sm:mb-20 px-2"
          data-testid="public-trial-hero"
        >
          {/* Salon brand on top — logo if uploaded, otherwise typeset name. */}
          {data.owner_logo_url && data.owner_logo_url.startsWith("data:image/") ? (
            <img
              src={data.owner_logo_url}
              alt={heroBrandName || "Brand"}
              className="mx-auto mb-10 sm:mb-12 max-h-16 sm:max-h-20 w-auto object-contain pt-fade-in pt-d-1"
              data-testid="public-trial-hero-logo"
            />
          ) : (
            heroBrandName && (
              <div
                className="font-serif-heading mb-10 sm:mb-12 pt-fade-in pt-d-1"
                style={{
                  color: "#4A4A4A",
                  fontSize: "clamp(1.4rem, 2.6vw, 1.85rem)",
                  letterSpacing: "0.04em",
                }}
                data-testid="public-trial-hero-brand"
              >
                {heroBrandName}
              </div>
            )
          )}

          <span
            className="label-uppercase block pt-fade-in pt-d-2"
            style={{ color: "#C8A97E", letterSpacing: "0.32em", fontSize: "11px" }}
          >
            <KindIcon className="w-3.5 h-3.5 inline mr-2 align-text-bottom" strokeWidth={1.5} />
            La tua prova {kindLabel}
          </span>

          <h1
            className="font-serif-heading mt-7 leading-[1.12] pt-fade-up pt-d-2"
            style={{
              color: "#2C2C2C",
              fontSize: "clamp(2.1rem, 4.6vw, 3.4rem)",
              fontStyle: "italic",
              fontWeight: 500,
              letterSpacing: "-0.01em",
              maxWidth: "22ch",
              margin: "1.75rem auto 0",
            }}
            data-testid="public-trial-title"
          >
            La tua prova sposa
          </h1>

          {brideName && (
            <p
              className="mt-7 pt-fade-in pt-d-3"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontStyle: "italic",
                color: "#A88B5C",
                fontSize: "clamp(1.25rem, 2vw, 1.55rem)",
                letterSpacing: "0.02em",
              }}
              data-testid="public-trial-name"
            >
              Per {brideName}
            </p>
          )}

          <p
            className="mx-auto mt-7 max-w-xl leading-relaxed pt-fade-up pt-d-4"
            style={{ color: "#6E6E6E", fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)" }}
            data-testid="public-trial-subtitle"
          >
            Un momento dedicato a creare il tuo look perfetto per il giorno più importante.
          </p>

          <div
            className="mx-auto mt-12 h-px w-12 pt-fade-in pt-d-5"
            style={{ backgroundColor: "#C8A97E", opacity: 0.55 }}
            aria-hidden="true"
          />

          {/* Date · time · location — refined inline meta */}
          {(data.date || data.time || data.location) && (
            <div
              className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3 pt-fade-up pt-d-5"
              style={{ color: "#6E6E6E", fontSize: "0.95rem" }}
              data-testid="public-trial-meta"
            >
              {data.date && (
                <span className="inline-flex items-center gap-2">
                  <Calendar className="w-4 h-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} />
                  {data.date}
                </span>
              )}
              {data.time && (
                <span className="inline-flex items-center gap-2">
                  <Clock className="w-4 h-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} />
                  {data.time}
                </span>
              )}
              {data.location && (
                <span className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} style={{ color: "#C8A97E" }} />
                  {data.location}
                </span>
              )}
            </div>
          )}
        </header>

        {/* === PHOTOS — image-led, almost full-width hero photo === */}
        {photos.length > 0 && (
          <section className="mb-16 sm:mb-20" data-testid="public-trial-photos">
            <button
              type="button"
              onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
              className="block w-full overflow-hidden group cursor-zoom-in"
              style={{
                borderRadius: "14px",
                boxShadow: "0 4px 12px rgba(74,74,74,0.06), 0 30px 80px -28px rgba(122,98,68,0.28)",
                backgroundColor: "#FFFDFA",
              }}
              data-testid="public-trial-photo-0"
            >
              <img
                src={photos[0]}
                alt={`Foto prova ${kindLabel} 1`}
                loading="eager"
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.015]"
                style={{ display: "block", maxHeight: "78vh" }}
              />
            </button>
            {/* Additional photos — quieter grid below the hero */}
            {photos.length > 1 && (
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {photos.slice(1).map((src, i) => {
                  const idx = i + 1;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                      className="aspect-[4/5] overflow-hidden group cursor-zoom-in"
                      style={{
                        borderRadius: "10px",
                        boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 14px 40px -22px rgba(122,98,68,0.18)",
                      }}
                      data-testid={`public-trial-photo-${idx}`}
                    >
                      <img
                        src={src}
                        alt={`Foto prova ${kindLabel} ${idx + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* === "Cosa abbiamo definito durante la prova" === */}
        <section
          className="mb-16 sm:mb-20 px-2"
          data-testid="public-trial-defined"
        >
          <div className="text-center">
            <span
              className="label-uppercase block"
              style={{ color: "#C8A97E", letterSpacing: "0.32em", fontSize: "10px" }}
            >
              Il Suo look
            </span>
            <h2
              className="font-serif-heading mt-4 mx-auto"
              style={{
                color: "#2C2C2C",
                fontSize: "clamp(1.75rem, 3.4vw, 2.4rem)",
                fontStyle: "italic",
                fontWeight: 500,
                lineHeight: 1.15,
                maxWidth: "20ch",
                letterSpacing: "-0.005em",
              }}
            >
              Cosa abbiamo definito durante la prova
            </h2>
            <div
              className="mx-auto mt-6 h-px w-10"
              style={{ backgroundColor: "#C8A97E", opacity: 0.5 }}
              aria-hidden="true"
            />
          </div>
          <ul
            className="mt-10 max-w-xl mx-auto space-y-5 list-none pl-0"
            data-testid="public-trial-defined-list"
          >
            {trialBullets.map((txt, i) => (
              <li
                key={i}
                className="flex gap-4 leading-relaxed"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontSize: "clamp(1.05rem, 1.6vw, 1.2rem)",
                  color: "#4A4A4A",
                  lineHeight: 1.65,
                }}
              >
                <span
                  style={{ color: "#C8A97E", flex: "none", marginTop: "4px" }}
                  aria-hidden="true"
                >
                  ✦
                </span>
                <span>{txt}</span>
              </li>
            ))}
          </ul>
        </section>
        {/* === Empty state (no photos) === */}
        {photos.length === 0 && (
          <section
            className="mb-16 sm:mb-20 rounded-md p-12 text-center"
            style={{
              backgroundColor: "#FFFDFA",
              boxShadow: "0 1px 2px rgba(74,74,74,0.04), 0 22px 60px -32px rgba(122,98,68,0.16)",
            }}
            data-testid="public-trial-empty"
          >
            <p
              className="italic"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "#7B6A45",
                fontSize: "1.15rem",
              }}
            >
              I dettagli della Sua prova le saranno condivisi a breve.
            </p>
          </section>
        )}

        {/* === Final emotional message — chiusura emozionale, ora vera fine
            della pagina (CTA preventivo intenzionalmente rimossa). Lo
            spacing superiore è leggermente più ampio per "respirare" e
            dare peso editoriale alla frase di chiusura. === */}
        <section
          className="mt-2 mb-16 px-4 text-center"
          data-testid="public-trial-emotional-closing"
        >
          {/* Sottile ornamento orizzontale sopra il cuore — agisce da
              "punto di chiusura" tipografico e separa visualmente la
              galleria dalla frase finale senza una grossa linea netta. */}
          <div
            className="mx-auto h-px w-10 mb-7"
            style={{ backgroundColor: "#E8DCC4", opacity: 0.7 }}
            aria-hidden="true"
          />
          <Heart
            className="w-5 h-5 mx-auto mb-6"
            strokeWidth={1.5}
            style={{ color: "#C8A97E" }}
            aria-hidden="true"
          />
          <p
            className="mx-auto leading-relaxed italic"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              color: "#7B6A45",
              fontSize: "clamp(1.15rem, 1.8vw, 1.4rem)",
              maxWidth: "44ch",
              lineHeight: 1.7,
            }}
          >
            Il nostro obiettivo è farti sentire perfetta e sicura nel tuo giorno più importante.
            Questa prova è il primo passo per costruire il tuo look su misura.
          </p>
          {heroBrandName && (
            <p
              className="mt-8 italic"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "#A88B5C",
                fontSize: "1rem",
                letterSpacing: "0.05em",
              }}
              data-testid="public-trial-signoff"
            >
              — con cura, {heroBrandName}
            </p>
          )}
        </section>

        {/* === Quiet platform credit === */}
        <div
          className="text-center mt-8"
          style={{
            color: "rgba(184,172,154,0.55)",
            fontSize: "9px",
            letterSpacing: "0.24em",
          }}
        >
          AI Bridal Manager™
        </div>
      </div>

      <PhotoLightbox
        open={lightboxOpen}
        photos={photos}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        alt={`Foto prova ${kindLabel}`}
      />
    </div>
  );
}
